import type React from "react";
import { Link } from "react-router";
import { Helmet } from "react-helmet-async";
import NearAILogo from "@/assets/icons/near-ai.svg?react";
import CloudflareLogo from "@/assets/images/cloudflare.svg?react";
import { APP_ROUTES } from "./routes";
import { blogPosts } from "./blog/posts";

const CONFIDE_URL = "https://confideai.ai/welcome/mycenai";

const SplashPage: React.FC = () => {
  return (
    <div className="flex min-h-screen w-full flex-col bg-white dark:bg-gray-950">
      <Helmet>
        <title>MycenAI — AI-Powered Support for Psychedelic-Assisted Therapy</title>
        <meta
          name="description"
          content="Confidential AI tools for psychedelic-assisted therapy practitioners. Evidence-based clinical templates, integration support, and research-backed resources — built on hardware-secured infrastructure."
        />
        <link rel="canonical" href="https://mycenai.org/" />
        <meta property="og:title" content="MycenAI — AI-Powered Support for Psychedelic-Assisted Therapy" />
        <meta property="og:description" content="Confidential AI tools for psychedelic-assisted therapy practitioners. Evidence-based clinical templates, integration support, and research-backed resources." />
        <meta property="og:url" content="https://mycenai.org/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "MycenAI",
            "url": "https://mycenai.org",
            "description": "AI-powered support for psychedelic-assisted therapy practitioners. Built on hardware-secured confidential computing infrastructure.",
            "foundingDate": "2026",
            "areaServed": "Worldwide",
            "sameAs": []
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="border-gray-200 border-b bg-white/80 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/80">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <span className="font-bold text-gray-900 text-xl dark:text-white">
            Mycen<span className="text-violet-600 dark:text-violet-400">AI</span>
          </span>
          <div className="flex items-center gap-6">
            <Link
              to={APP_ROUTES.BLOG}
              className="hidden font-medium text-gray-700 text-sm transition hover:text-violet-600 sm:inline dark:text-gray-300 dark:hover:text-violet-400"
            >
              Blog
            </Link>
            <Link
              to={APP_ROUTES.RESOURCES}
              className="hidden font-medium text-gray-700 text-sm transition hover:text-violet-600 sm:inline dark:text-gray-300 dark:hover:text-violet-400"
            >
              Resources
            </Link>
            <Link
              to={APP_ROUTES.ABOUT}
              className="hidden font-medium text-gray-700 text-sm transition hover:text-violet-600 sm:inline dark:text-gray-300 dark:hover:text-violet-400"
            >
              About
            </Link>
            <a
              href={CONFIDE_URL}
              className="rounded-lg bg-violet-600 px-6 py-2.5 font-semibold text-sm text-white shadow-sm transition hover:bg-violet-700"
            >
              Get Started
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {/* Hero Section */}
        <section className="border-gray-100 border-b bg-gradient-to-b from-white to-violet-50/30 px-6 py-20 sm:py-24 lg:py-32 dark:border-gray-800 dark:from-gray-950 dark:to-violet-950/10">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="mb-6 font-bold text-4xl text-gray-900 leading-tight tracking-tight sm:text-5xl lg:text-6xl dark:text-white">
              AI-Powered Support for Psychedelic-Assisted Therapy
            </h1>
            <p className="mb-8 text-gray-600 text-lg leading-relaxed sm:text-xl dark:text-gray-300">
              Clinical documentation tools designed for PAT practitioners. Evidence-based templates for preparation, ceremony support, and integration — built on confidential computing infrastructure.
            </p>
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <a
                href={CONFIDE_URL}
                className="rounded-lg bg-violet-600 px-8 py-4 font-semibold text-base text-white shadow-lg transition hover:bg-violet-700 hover:shadow-xl"
              >
                Get Started Free
              </a>
              <p className="text-gray-500 text-sm dark:text-gray-400">
                Powered by ConfideAI's secure platform
              </p>
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="border-gray-100 border-b px-6 py-16 sm:py-20 dark:border-gray-800">
          <div className="mx-auto max-w-6xl">
            <div className="mb-12 text-center">
              <h2 className="mb-4 font-bold text-3xl text-gray-900 sm:text-4xl dark:text-white">
                Confidentiality at the Core
              </h2>
              <p className="mx-auto max-w-2xl text-gray-600 text-lg dark:text-gray-300">
                Psychedelic therapy demands the highest standard of privacy. MycenAI uses hardware-secured enclaves — your clinical content is never stored, never shared, never used for training.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900/30">
                  <svg className="h-6 w-6 text-violet-600 dark:text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="mb-2 font-semibold text-gray-900 dark:text-white">
                  Hardware-Secured Processing
                </h3>
                <p className="text-gray-600 text-sm dark:text-gray-400">
                  AI runs inside Trusted Execution Environments (TEE) — hardware-protected enclaves that even the cloud provider cannot access during processing.
                </p>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900/30">
                  <svg className="h-6 w-6 text-violet-600 dark:text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="mb-2 font-semibold text-gray-900 dark:text-white">
                  Cryptographic Proof
                </h3>
                <p className="text-gray-600 text-sm dark:text-gray-400">
                  TEE attestation provides verifiable proof that your data was processed in a genuine secure enclave. Trust backed by hardware, not promises.
                </p>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900/30">
                  <svg className="h-6 w-6 text-violet-600 dark:text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
                <h3 className="mb-2 font-semibold text-gray-900 dark:text-white">
                  Zero Data Retention
                </h3>
                <p className="text-gray-600 text-sm dark:text-gray-400">
                  No clinical content stored on our servers. No training on your data. No third-party access. Your sessions remain yours.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="border-gray-100 border-b bg-gray-50 px-6 py-16 sm:py-20 dark:border-gray-800 dark:bg-gray-900/50">
          <div className="mx-auto max-w-6xl">
            <div className="mb-12 text-center">
              <h2 className="mb-4 font-bold text-3xl text-gray-900 sm:text-4xl dark:text-white">
                Built for PAT Practitioners
              </h2>
              <p className="mx-auto max-w-2xl text-gray-600 text-lg dark:text-gray-300">
                Clinical tools designed for the unique demands of psychedelic-assisted therapy
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900/30">
                  <svg className="h-6 w-6 text-violet-600 dark:text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="mb-2 font-semibold text-gray-900 dark:text-white">
                  Integration Session Notes
                </h3>
                <p className="text-gray-600 text-sm dark:text-gray-400">
                  Structured templates for preparation, ceremony support, and integration sessions — the three pillars of PAT practice.
                </p>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900/30">
                  <svg className="h-6 w-6 text-violet-600 dark:text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </div>
                <h3 className="mb-2 font-semibold text-gray-900 dark:text-white">
                  Orientation-Aware
                </h3>
                <p className="text-gray-600 text-sm dark:text-gray-400">
                  AI adapts to your therapeutic modality — somatic, transpersonal, IFS, psychodynamic, humanistic, and 10+ more orientations.
                </p>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900/30">
                  <svg className="h-6 w-6 text-violet-600 dark:text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="mb-2 font-semibold text-gray-900 dark:text-white">
                  Document Chaining
                </h3>
                <p className="text-gray-600 text-sm dark:text-gray-400">
                  Output from one template feeds into the next — intake to treatment plan to session notes to discharge, building a complete clinical record.
                </p>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900/30">
                  <svg className="h-6 w-6 text-violet-600 dark:text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="mb-2 font-semibold text-gray-900 dark:text-white">
                  Research-Backed
                </h3>
                <p className="text-gray-600 text-sm dark:text-gray-400">
                  Every template grounded in peer-reviewed clinical literature with academic citations. Evidence-based, not generic.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="border-gray-100 border-b px-6 py-16 sm:py-20 dark:border-gray-800">
          <div className="mx-auto max-w-6xl">
            <div className="mb-12 text-center">
              <h2 className="mb-4 font-bold text-3xl text-gray-900 sm:text-4xl dark:text-white">
                How It Works
              </h2>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-violet-100 font-bold text-2xl text-violet-600 dark:bg-violet-900/30 dark:text-violet-400">
                    1
                  </div>
                </div>
                <h3 className="mb-2 font-semibold text-gray-900 text-lg dark:text-white">
                  Choose Your Context
                </h3>
                <p className="text-gray-600 text-sm dark:text-gray-400">
                  Select a clinical template — preparation notes, integration session, treatment plan, assessment, or correspondence.
                </p>
              </div>

              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-violet-100 font-bold text-2xl text-violet-600 dark:bg-violet-900/30 dark:text-violet-400">
                    2
                  </div>
                </div>
                <h3 className="mb-2 font-semibold text-gray-900 text-lg dark:text-white">
                  Input Brief Notes
                </h3>
                <p className="text-gray-600 text-sm dark:text-gray-400">
                  Short key phrases are enough. Paste from session notes, type bullet points, or upload documents — processed locally in your browser.
                </p>
              </div>

              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-violet-100 font-bold text-2xl text-violet-600 dark:bg-violet-900/30 dark:text-violet-400">
                    3
                  </div>
                </div>
                <h3 className="mb-2 font-semibold text-gray-900 text-lg dark:text-white">
                  Review & Refine
                </h3>
                <p className="text-gray-600 text-sm dark:text-gray-400">
                  Get clinically appropriate documentation in seconds. Adjust tone and detail, then chain into your next document.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="border-gray-100 border-b bg-violet-50 px-6 py-16 sm:py-20 dark:border-gray-800 dark:bg-violet-950/20">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-4 font-bold text-3xl text-gray-900 sm:text-4xl dark:text-white">
              Ready to Streamline Your PAT Practice?
            </h2>
            <p className="text-gray-700 text-lg dark:text-gray-300">
              Join practitioners using confidential AI to reduce documentation burden and focus on what matters — your clients' healing journey.
            </p>
            <div className="mt-8">
              <a
                href={CONFIDE_URL}
                className="rounded-lg bg-violet-600 px-8 py-4 font-semibold text-base text-white shadow-lg transition hover:bg-violet-700 hover:shadow-xl"
              >
                Get Started Free
              </a>
            </div>
          </div>
        </section>

        {/* From Our Blog Section */}
        <section className="border-gray-100 border-b px-6 py-16 sm:py-20 dark:border-gray-800">
          <div className="mx-auto max-w-6xl">
            <div className="mb-8 flex items-center justify-between">
              <h2 className="font-bold text-2xl text-gray-900 sm:text-3xl dark:text-white">
                From Our Blog
              </h2>
              <Link
                to={APP_ROUTES.BLOG}
                className="font-medium text-sm text-violet-600 transition hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300"
              >
                View all →
              </Link>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              {blogPosts.slice(0, 2).map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group rounded-xl border border-gray-200 p-6 transition hover:border-violet-300 hover:shadow-sm dark:border-gray-700 dark:hover:border-violet-600"
                >
                  <p className="mb-2 text-gray-500 text-sm dark:text-gray-400">
                    {post.date}
                  </p>
                  <h3 className="mb-2 font-semibold text-gray-900 text-lg leading-snug transition group-hover:text-violet-600 dark:text-white dark:group-hover:text-violet-400">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed dark:text-gray-400">
                    {post.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-gray-200 border-t bg-gray-50 px-6 py-12 dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col justify-between gap-12 md:flex-row">
            {/* Brand Section */}
            <div className="max-w-sm">
              <div className="mb-4">
                <span className="font-bold text-gray-900 text-xl dark:text-white">
                  Mycen<span className="text-violet-600 dark:text-violet-400">AI</span>
                </span>
              </div>
              <p className="mb-6 text-gray-600 text-sm leading-relaxed dark:text-gray-400">
                AI-powered support for psychedelic-assisted therapy practitioners.
                <br />
                Confidential, clinical, evidence-based.
              </p>
              <p className="text-gray-500 text-sm dark:text-gray-500">
                © 2026 MycenAI. All rights reserved.
              </p>
            </div>

            {/* Links Section */}
            <div className="grid grid-cols-2 gap-x-12 gap-y-8 sm:flex sm:gap-20">
              <div>
                <h4 className="mb-4 font-semibold text-gray-900 dark:text-white">
                  Resources
                </h4>
                <ul className="space-y-3 text-gray-600 text-sm dark:text-gray-400">
                  <li>
                    <Link
                      to={APP_ROUTES.BLOG}
                      className="transition hover:text-violet-600 dark:hover:text-violet-400"
                    >
                      Blog
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={APP_ROUTES.ABOUT}
                      className="transition hover:text-violet-600 dark:hover:text-violet-400"
                    >
                      About
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={APP_ROUTES.RESOURCES}
                      className="transition hover:text-violet-600 dark:hover:text-violet-400"
                    >
                      Clinical Resources
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={APP_ROUTES.FAQ}
                      className="transition hover:text-violet-600 dark:hover:text-violet-400"
                    >
                      FAQ
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="mb-4 font-semibold text-gray-900 dark:text-white">
                  Legal
                </h4>
                <ul className="space-y-3 text-gray-600 text-sm dark:text-gray-400">
                  <li>
                    <Link
                      to={APP_ROUTES.PRIVACY}
                      className="transition hover:text-violet-600 dark:hover:text-violet-400"
                    >
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={APP_ROUTES.TERMS}
                      className="transition hover:text-violet-600 dark:hover:text-violet-400"
                    >
                      Terms & Conditions
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="mb-4 font-semibold text-gray-900 dark:text-white">
                  Platform
                </h4>
                <ul className="space-y-3 text-gray-600 text-sm dark:text-gray-400">
                  <li>
                    <a
                      href={CONFIDE_URL}
                      className="transition hover:text-violet-600 dark:hover:text-violet-400"
                    >
                      Open ConfideAI
                    </a>
                  </li>
                  <li>
                    <a
                      href="mailto:support@confideai.ai"
                      className="transition hover:text-violet-600 dark:hover:text-violet-400"
                    >
                      support@confideai.ai
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Technology Stack Attribution */}
          <div className="mt-16 border-gray-200 border-t pt-8 dark:border-gray-700">
            <p className="mb-6 text-center font-medium text-gray-500 text-xs uppercase tracking-wider dark:text-gray-400">
              Powered by
            </p>
            <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12">
              <a
                href="https://confideai.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center gap-2 transition-opacity hover:opacity-100"
                aria-label="ConfideAI"
              >
                <span className="font-semibold text-gray-400 text-lg opacity-50 transition-all group-hover:opacity-100 dark:text-gray-500 dark:group-hover:text-gray-300">ConfideAI</span>
              </a>

              <a
                href="https://near.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center gap-2 transition-opacity hover:opacity-100"
                aria-label="NEAR AI"
              >
                <NearAILogo className="h-8 opacity-50 grayscale transition-all group-hover:opacity-100 group-hover:grayscale-0 dark:opacity-40 dark:invert dark:group-hover:opacity-100" />
                <span className="text-center text-gray-400 text-xs group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300">NEAR AI</span>
              </a>

              <a
                href="https://www.cloudflare.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center gap-2 transition-opacity hover:opacity-100"
                aria-label="Cloudflare"
              >
                <CloudflareLogo className="h-8 opacity-50 grayscale transition-all group-hover:opacity-100 group-hover:grayscale-0 dark:opacity-40 dark:invert dark:group-hover:opacity-100" />
                <span className="text-center text-gray-400 text-xs group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300">Cloudflare</span>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SplashPage;
